import { useState } from 'react';
import { Link } from 'react-router-dom';
import { InlineEditField } from '@/components/shared/InlineEditField';
import { InlineSelect } from '@/components/shared/InlineSelect';
import { DatePicker } from '@/components/ui/date-picker';
import { Panel } from '@/components/ui/panel';
import { Button } from '@/components/ui/button';
import { INVOICE_TYPES, type InvoiceType } from '@jobos/shared/types/invoice';
import {
  type JobCardStatus,
  type JobInvoiceStatus,
  JOB_CARD_STATUSES,
  JOB_INVOICE_STATUSES,
} from '@jobos/shared/types/job';
import { JOB_CARD_STATUS_CONFIG, JOB_INVOICE_STATUS_CONFIG } from '@jobos/shared';
import { StatusStepper, type StatusStepperStep } from '@/components/shared/StatusStepper';
import { useBillingAddresses, useCreateBillingAddress } from '@/hooks/useBillingAddresses';
import { AddressInput } from '@/components/shared/AddressInput';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { confirmInvoiceTransition } from './confirmInvoiceTransition';

interface JobBillingCardProps {
  clientId: string;
  jobId: string;
  jobCardStatus: JobCardStatus;
  invoiceStatus: JobInvoiceStatus;
  lastInvoicedDate: string | null | undefined;
  invoicePaidDate: string | null | undefined;
  invoiceType: InvoiceType | null | undefined;
  invoiceNumber: string | null | undefined;
  billingAddressLabel: string | null | undefined;
  billingAddressLine1: string | null | undefined;
  billingAddressLine2: string | null | undefined;
  billingCity: string | null | undefined;
  billingState: string | null | undefined;
  billingZip: string | null | undefined;
  onUpdateField: (field: string, value: any) => Promise<void>;
}

const JOB_CARD_STATUS_STEPS: StatusStepperStep<JobCardStatus>[] = JOB_CARD_STATUSES.map((value) => ({
  value,
  label: JOB_CARD_STATUS_CONFIG[value].label,
  className: JOB_CARD_STATUS_CONFIG[value].className,
}));

const JOB_INVOICE_STATUS_STEPS: StatusStepperStep<JobInvoiceStatus>[] = JOB_INVOICE_STATUSES.map((value) => ({
  value,
  label: JOB_INVOICE_STATUS_CONFIG[value].label,
  className: JOB_INVOICE_STATUS_CONFIG[value].className,
}));

function formatDateForDb(date: Date | undefined): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatBillingAddress(
  line1: string | null | undefined,
  line2: string | null | undefined,
  city: string | null | undefined,
  state: string | null | undefined,
  zip: string | null | undefined,
): string[] {
  const lines: string[] = [];
  if (line1) lines.push(line1);
  if (line2) lines.push(line2);
  const cityStateZip = [city, state].filter(Boolean).join(', ');
  const withZip = [cityStateZip, zip].filter(Boolean).join(' ');
  if (withZip) lines.push(withZip);
  return lines;
}

export function JobBillingCard({
  clientId,
  jobId,
  jobCardStatus,
  invoiceStatus,
  lastInvoicedDate,
  invoicePaidDate,
  invoiceType,
  invoiceNumber,
  billingAddressLabel,
  billingAddressLine1,
  billingAddressLine2,
  billingCity,
  billingState,
  billingZip,
  onUpdateField,
}: JobBillingCardProps) {
  const [isChangingAddress, setIsChangingAddress] = useState(false);
  const { data: addresses } = useBillingAddresses(clientId);

  // Local draft so typing stays responsive: the billing* props are backed by
  // debounced, server-round-tripped state, so binding the inputs directly to
  // them drops characters. Seed the draft whenever the editor is opened.
  const buildAddressDraft = () => ({
    addressLine1: billingAddressLine1 || '',
    addressLine2: billingAddressLine2 || '',
    city: billingCity || '',
    state: billingState || '',
    zip: billingZip || '',
  });
  const [addressDraft, setAddressDraft] = useState(buildAddressDraft);

  const openAddressEditor = () => {
    setAddressDraft(buildAddressDraft());
    setShowSavePrompt(false);
    setNewAddressLabel('');
    setIsChangingAddress(true);
  };

  // On "Done", offer to save the address to the client's saved list — but only
  // when it's a genuinely new entry: something was typed, and it isn't already
  // in the list (which also covers addresses picked from the saved dropdown).
  const createBillingAddress = useCreateBillingAddress();
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [newAddressLabel, setNewAddressLabel] = useState('');

  const draftIsEmpty =
    !addressDraft.addressLine1 &&
    !addressDraft.addressLine2 &&
    !addressDraft.city &&
    !addressDraft.state &&
    !addressDraft.zip;

  const draftAlreadySaved = (addresses ?? []).some(
    (a) =>
      (a.addressLine1 || '') === addressDraft.addressLine1 &&
      (a.addressLine2 || '') === addressDraft.addressLine2 &&
      (a.city || '') === addressDraft.city &&
      (a.state || '') === addressDraft.state &&
      (a.zip || '') === addressDraft.zip,
  );

  const handleDone = () => {
    if (!draftIsEmpty && !draftAlreadySaved) {
      setNewAddressLabel('');
      setShowSavePrompt(true);
      return;
    }
    setIsChangingAddress(false);
  };

  const closeEditor = () => {
    setShowSavePrompt(false);
    setNewAddressLabel('');
    setIsChangingAddress(false);
  };

  const handleConfirmSave = () => {
    const trimmedLabel = newAddressLabel.trim();
    createBillingAddress.mutate({
      clientId,
      label: trimmedLabel || undefined,
      addressLine1: addressDraft.addressLine1 || undefined,
      addressLine2: addressDraft.addressLine2 || undefined,
      city: addressDraft.city || undefined,
      state: addressDraft.state || undefined,
      zip: addressDraft.zip || undefined,
    });
    // Reflect the label on the job's collapsed view too.
    if (trimmedLabel) void onUpdateField('billingAddressLabel', trimmedLabel);
    closeEditor();
  };

  const hasBillingAddress =
    billingAddressLine1 || billingAddressLine2 || billingCity || billingState || billingZip;
  const addressLines = formatBillingAddress(
    billingAddressLine1,
    billingAddressLine2,
    billingCity,
    billingState,
    billingZip,
  );

  const handleJobCardStatusChange = async (next: JobCardStatus) => {
    await onUpdateField('jobCardStatus', next);
  };

  const handleInvoiceStatusChange = async (next: JobInvoiceStatus) => {
    await onUpdateField('invoiceStatus', next);
    // Server auto-fills lastInvoicedDate when transitioning into invoice_sent
    // and the field is currently NULL — handled in PUT /api/jobs/:id.
    // Confirmed transitions that clear dates emit explicit nulls below.
    const wasSent =
      invoiceStatus === 'invoice_sent' ||
      invoiceStatus === 'partially_paid' ||
      invoiceStatus === 'paid';
    const willBeSent = next === 'invoice_sent' || next === 'partially_paid' || next === 'paid';
    if (wasSent && !willBeSent && lastInvoicedDate) {
      await onUpdateField('lastInvoicedDate', null);
    }
    if (invoiceStatus === 'paid' && next !== 'paid' && invoicePaidDate) {
      await onUpdateField('invoicePaidDate', null);
    }
  };

  const showSentDate =
    invoiceStatus === 'invoice_sent' ||
    invoiceStatus === 'partially_paid' ||
    invoiceStatus === 'paid';
  const showPaidDate = invoiceStatus === 'paid';

  return (
    <Panel title="Billing">
      {/* Zone 1: Job Card Status */}
      <div>
        <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase">Job Card Status</h3>
        <StatusStepper
          steps={JOB_CARD_STATUS_STEPS}
          value={jobCardStatus}
          onChange={handleJobCardStatusChange}
          ariaLabel="Job card status"
        />
        <div className="mt-2">
          <Link
            to={`/jobs/${jobId}?tab=breakdown`}
            className="text-sm text-blue-600 hover:underline"
          >
            Open breakdown page →
          </Link>
        </div>
      </div>

      {/* Zone 2: Invoice Status */}
      <div className="border-t pt-4">
        <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase">Invoice Status</h3>
        <StatusStepper
          steps={JOB_INVOICE_STATUS_STEPS}
          value={invoiceStatus}
          onChange={handleInvoiceStatusChange}
          ariaLabel="Invoice status"
          confirmRequiredOn={(from, to) =>
            confirmInvoiceTransition(from, to, lastInvoicedDate, invoicePaidDate)
          }
        />
        {(showSentDate || showPaidDate) && (
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4">
            {showSentDate && (
              <div className="flex-1">
                <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">
                  Sent date
                </label>
                <DatePicker
                  value={lastInvoicedDate}
                  onSave={(date) => onUpdateField('lastInvoicedDate', formatDateForDb(date))}
                  placeholder="Set date"
                />
              </div>
            )}
            {showPaidDate && (
              <div className="flex-1">
                <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">
                  Paid date
                </label>
                <DatePicker
                  value={invoicePaidDate}
                  onSave={(date) => onUpdateField('invoicePaidDate', formatDateForDb(date))}
                  placeholder="(date unknown)"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Zone 3: Invoice Information */}
      <div className="border-t pt-4">
        <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase">Invoice Information</h3>
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
          <div className="flex-1">
            <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">
              Invoice Type
            </label>
            <InlineSelect
              value={invoiceType || 'quickbooks'}
              options={INVOICE_TYPES.map((type) => ({
                value: type,
                label: type.charAt(0).toUpperCase() + type.slice(1),
              }))}
              onSave={(value) => onUpdateField('invoiceType', value as InvoiceType)}
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">
              Invoice Number
            </label>
            <InlineEditField
              value={invoiceNumber || ''}
              onSave={(value) => onUpdateField('invoiceNumber', value || null)}
              placeholder="Add invoice number..."
              editTrigger="click"
            />
          </div>
        </div>
      </div>

      {/* Zone 4: Billing Address */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-gray-700 uppercase">Billing Address</h3>
          {!isChangingAddress && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-3 text-xs sm:h-6 sm:px-2"
              onClick={openAddressEditor}
            >
              {hasBillingAddress ? 'Change' : '+ Add'}
            </Button>
          )}
        </div>
        {isChangingAddress ? (
          <div className="space-y-3">
            {addresses && addresses.length > 0 && (
              <Select
                onValueChange={async (addressId: string) => {
                  const addr = addresses.find((a) => a.id === addressId);
                  if (addr) {
                    setAddressDraft({
                      addressLine1: addr.addressLine1 || '',
                      addressLine2: addr.addressLine2 || '',
                      city: addr.city || '',
                      state: addr.state || '',
                      zip: addr.zip || '',
                    });
                    await Promise.all([
                      onUpdateField('billingAddressLabel', addr.label || null),
                      onUpdateField('billingAddressLine1', addr.addressLine1 || null),
                      onUpdateField('billingAddressLine2', addr.addressLine2 || null),
                      onUpdateField('billingCity', addr.city || null),
                      onUpdateField('billingState', addr.state || null),
                      onUpdateField('billingZip', addr.zip || null),
                    ]);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick from saved addresses..." />
                </SelectTrigger>
                <SelectContent>
                  {addresses.map((addr) => {
                    const parts = [addr.addressLine1, addr.city, addr.state].filter(Boolean);
                    return (
                      <SelectItem key={addr.id} value={addr.id}>
                        <span className="font-medium">{addr.label || 'Untitled'}</span>
                        {addr.isDefault && !addr.managerId && (
                          <span className="text-gray-500"> (Default)</span>
                        )}
                        {parts.length > 0 && (
                          <span className="text-gray-500"> — {parts.join(', ')}</span>
                        )}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            <AddressInput
              value={addressDraft}
              onChange={(addr) => {
                // Update the local draft synchronously so typing is responsive,
                // then persist (debounced upstream) in the background.
                setAddressDraft({
                  addressLine1: addr.addressLine1 || '',
                  addressLine2: addr.addressLine2 || '',
                  city: addr.city || '',
                  state: addr.state || '',
                  zip: addr.zip || '',
                });
                void Promise.all([
                  onUpdateField('billingAddressLine1', addr.addressLine1 || null),
                  onUpdateField('billingAddressLine2', addr.addressLine2 || null),
                  onUpdateField('billingCity', addr.city || null),
                  onUpdateField('billingState', addr.state || null),
                  onUpdateField('billingZip', addr.zip || null),
                ]);
              }}
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={handleDone}>
                Done
              </Button>
            </div>
          </div>
        ) : hasBillingAddress ? (
          <div className="text-sm text-gray-900">
            {billingAddressLabel && <div className="font-semibold">{billingAddressLabel}</div>}
            {addressLines.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No billing address set</p>
        )}
      </div>

      <Dialog open={showSavePrompt} onOpenChange={(open) => !open && closeEditor()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save to client's addresses?</DialogTitle>
            <DialogDescription>
              Add this address to the client's saved list so you can reuse it on other jobs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
              {formatBillingAddress(
                addressDraft.addressLine1,
                addressDraft.addressLine2,
                addressDraft.city,
                addressDraft.state,
                addressDraft.zip,
              ).map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
              <Input
                value={newAddressLabel}
                onChange={(e) => setNewAddressLabel(e.target.value)}
                placeholder="Label (optional, e.g. Main office)"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeEditor}>
              Don't save
            </Button>
            <Button onClick={handleConfirmSave} disabled={createBillingAddress.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
